import React, { useState, useEffect } from 'react';
import { MapPin } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { apiService } from '../services/api';

// Разбиение по тирам, как принято в гемблинге: тир определяет ценность
// трафика, а не размер страны. Первый — зрелые регулируемые рынки с высоким
// средним депозитом, второй — растущие и средние по платёжеспособности,
// третий — всё остальное.
//
// Внутри тира Европа идёт первой: по ней больше всего работы.
const TIER_1 = ["GB", "IE", "DE", "AT", "CH", "NL", "BE", "LU", "SE", "NO",
                "DK", "FI", "IS", "FR", "IT", "ES", "CA", "AU", "NZ", "US", "JP"];
const TIER_2 = ["PT", "GR", "CZ", "PL", "HU", "RO", "SK", "SI", "HR", "EE",
                "LV", "LT", "MT", "CY", "BG", "RS", "IL", "AE", "SA", "QA",
                "KW", "BH", "OM", "TR", "SG", "HK", "TW", "KR", "BR", "MX",
                "AR", "CL", "UY", "CR", "PA"];

const TIER_ORDER = ["Тир 1", "Тир 2", "Тир 3"];
const REGION_ORDER = ["Europe", "Middle East", "Americas", "Asia", "Oceania", "Africa", "Other"];

function tierOf(code) {
  const c = String(code || "").toUpperCase();
  if (TIER_1.includes(c)) return "Тир 1";
  if (TIER_2.includes(c)) return "Тир 2";
  return "Тир 3";
}

export const GeoFilter = ({ selectedGeo, onGeoChange, geosWithData = [], geosWithSpikes = [], geoSpikeCounts = {} }) => {
  const [activeGeos, setActiveGeos] = useState([]);
  const [allGeosCatalog, setAllGeosCatalog] = useState([]);

  useEffect(() => {
    const loadGeos = async () => {
      try {
        const res = await apiService.getSettings();
        if (res.data && res.data.active_geos) {
          setActiveGeos(res.data.active_geos);
        }
        if (res.data && Array.isArray(res.data.all_geos)) {
          setAllGeosCatalog(res.data.all_geos);
        }
      } catch (err) {
        console.error('Failed to load GEOs:', err);
        setActiveGeos(['BR','DE','FR','IT','ES','PL','ID','TH','VN','IN']);
        setAllGeosCatalog([]);
      }
    };
    loadGeos();
  }, []);

  const geoByCode = {};
  allGeosCatalog.forEach(g => {
    if (g?.code) geoByCode[String(g.code).toUpperCase()] = g;
  });

  // Группируем по тиру, внутри тира — по региону с Европой впереди.
  const grouped = {};
  activeGeos.forEach((codeRaw) => {
    const code = String(codeRaw || '').toUpperCase();
    if (!code) return;
    const meta = geoByCode[code] || { code, name: code, region: 'Other' };
    const tier = tierOf(code);
    if (!grouped[tier]) grouped[tier] = [];
    grouped[tier].push({ code, name: meta.name || code, region: meta.region || 'Other' });
  });
  Object.keys(grouped).forEach(tier => {
    grouped[tier].sort((a, b) => {
      const ra = REGION_ORDER.indexOf(a.region) < 0 ? 99 : REGION_ORDER.indexOf(a.region);
      const rb = REGION_ORDER.indexOf(b.region) < 0 ? 99 : REGION_ORDER.indexOf(b.region);
      if (ra !== rb) return ra - rb;
      return a.code.localeCompare(b.code);
    });
  });

  // Состояние страны определяется в одном месте. Раньше цвет задавали две
  // независимые функции: одна красила в зелёный по «есть данные», другая в
  // голубой — в итоге зелёным светились все страны подряд, и отличить страну
  // со всплесками от страны с любыми данными было нельзя.
  // Палитра проекта: зелёный — всплески, синий — есть данные, серый — пусто.
  // Ломался не цвет, а условие: страна считалась «со всплесками» при одном
  // всплеске за период, а сбор идёт по всем активным гео, поэтому зелёным
  // становилось всё поле. Порог — медиана по странам со всплесками: выше неё
  // страна действительно выделяется, ниже она просто с данными.
  const spikeCounts = geoSpikeCounts || {};
  const counts = Object.values(spikeCounts).map(Number).filter((n) => n > 0).sort((a, b) => a - b);
  const median = counts.length ? counts[Math.floor(counts.length / 2)] : 0;

  const geoState = (code) => {
    if (selectedGeo === code) return 'selected';
    const n = Number(spikeCounts[code] || 0);
    if (median > 0) {
      if (n >= median) return 'spikes';
      if (n > 0 || geosWithData.includes(code)) return 'data';
      return 'none';
    }
    if (geosWithSpikes.includes(code)) return 'spikes';
    if (geosWithData.includes(code)) return 'data';
    return 'none';
  };

  // Цвет ведёт className, поэтому вариант везде нейтральный, кроме выбранной.
  const getBadgeStyle = (code) => (geoState(code) === 'selected' ? 'default' : 'outline');

  const GEO_STATE_CLASS = {
    selected: 'cursor-pointer',
    spikes: 'cursor-pointer bg-emerald-50 text-emerald-700 border-emerald-300 hover:bg-emerald-100',
    data: 'cursor-pointer bg-sky-50 text-sky-700 border-sky-300 hover:bg-sky-100',
    none: 'cursor-pointer text-muted-foreground border-border opacity-60 hover:opacity-100',
  };

  const getBadgeClass = (code) => GEO_STATE_CLASS[geoState(code)];

  return (
    <Card className="card-elevated">
      <CardHeader>
        <CardTitle className="text-lg font-display flex items-center gap-2">
          <MapPin className="w-5 h-5 text-primary" />
          Filter by GEO
        </CardTitle>
        <div className="flex gap-3 text-xs text-muted-foreground mt-1">
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500"></span> spikes</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-sky-500"></span> data</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-zinc-600"></span> no data</span>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div>
            <p className="text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wider">ALL</p>
            <Badge
              variant={selectedGeo === null ? 'default' : 'outline'}
              className="cursor-pointer"
              onClick={() => onGeoChange(null)}
            >
              All Regions
            </Badge>
          </div>
          {TIER_ORDER.filter(t => grouped[t]).map((region) => { const geos = grouped[region];
            if (!geos.length) return null;
            const codes = geos.map(g => g.code);
            const withData = codes.filter(c => geosWithData.includes(c)).length;
            // Раньше показывали, у скольких стран региона «есть всплески» —
            // а это почти все страны, поэтому число было одинаковым везде.
            const regionBrands = codes.reduce((sum, c) => sum + Number(spikeCounts[c] || 0), 0);
            return (
              <div key={region}>
                <p className="text-xs font-semibold text-muted-foreground mb-2 tracking-wide">
                  {region} <span className="text-muted-foreground/70">· {geos.length}</span>
                  {' '}{regionBrands > 0
                    ? <span className="text-emerald-700">({regionBrands.toLocaleString()} brands with spikes)</span>
                    : withData > 0 && <span className="text-sky-600">({withData} with data)</span>}
                </p>
                <div className="flex flex-wrap gap-2">
                  {geos.map(({ code, name }) => (
                    <Badge
                      key={code}
                      variant={selectedGeo === code ? 'default' : 'outline'}
                      className={getBadgeClass(code)}
                      onClick={() => onGeoChange(code)}
                    >
                      {code} - {name || code}
                    </Badge>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};
